import $ from 'cash-dom';

/**
 * Contains methods for animation, that `cash-dom` doesn't have.
 * >Note: add more method if need.
 */
const animate = {
	/**
	 * Fades in.
	 * @param {string|Cash} selector String selector or Cache object.
	 * @param {number} transitionTime Transition time in ms.
	 */
	fadeIn(selector, transitionTime = 200) {
		const $selector = $(selector);

		if (!$selector.length) {
			return;
		}

		$selector
			.css('opacity', 0)
			.show()
			.css('transition', 'opacity ' + transitionTime + 'ms');

		setTimeout(() => $selector.css('opacity', 1), 0);
	},

	/**
	 * Fades out.
	 * @param {string|Cash} selector String selector or Cash object.
	 * @param {number} transitionTime Transition time in ms.
	 * @param {Function|null} callback
	 */
	fadeOut: function (selector, transitionTime = 200, callback = null) {
		const $selector = $(selector);

		$selector
			.css('transition', 'opacity ' + transitionTime + 'ms')
			.css('opacity', 0);

		setTimeout(() => {
			$selector.hide();

			if (typeof callback === 'function') {
				callback();
			}
		}, transitionTime);
	},

	/**
	 * Fade replace.
	 * @param {Cash} $currentElement Current Cash to be replaced (fadeOut).
	 * @param {Cash} $newElement New Cash to be set (fadeIn).
	 * @param {number} transitionTime Transition time in ms.
	 * @param {boolean} slide
	 */
	fadeReplace($currentElement, $newElement, transitionTime = 200, slide = false) {
		if (!$currentElement || !$currentElement.length) {
			$newElement.css('opacity', 0);
			this.fadeIn($newElement, transitionTime);
			return;
		}

		if (slide) {
			const $container = $currentElement.parent();

			const oldHeight = $currentElement.outerHeight(true);

			$container
				.css('position', 'relative')
				.css('height', oldHeight);

			$newElement
				.css({
					opacity: 0, position: 'absolute', left: 0, right: 0, top: 0
				});

			$container.append($newElement);

			const newHeight = $newElement.outerHeight(true);

			$container.css('transition', 'height ' + transitionTime + 'ms');
			$container[0].offsetHeight;
			$container.css('height', newHeight);

			this.fadeOut($currentElement, transitionTime);
			this.fadeIn($newElement, transitionTime);

			setTimeout(() => {
				$currentElement.remove();
				$newElement.css({position: '', left: '', right: '', top: ''});
				$container.css({height: '', transition: ''});
			}, transitionTime);
		} else {
			if ($currentElement.length > 1) {
				const $elToReplace = $currentElement.last();
				$currentElement.not($elToReplace).remove();
				$currentElement = $elToReplace;
			}

			$newElement.css('opacity', 0);

			$currentElement.after($newElement);

			this.fadeOut($currentElement, transitionTime);
			this.fadeIn($newElement, transitionTime);

			setTimeout(() => {
				$currentElement.remove();
			}, transitionTime);
		}
	},

    /**
     * Fade replace with horizontal slide to the left side.
     * @param {Cash} $currentElement Current Cash to be replaced (fadeOut).
     * @param {Cash} $newElement New Cash to be set (fadeIn).
     * @param {number} transitionTime Transition time in ms.
     */
    fadeReplaceWithHorizontalSlide($currentElement, $newElement, transitionTime = 200) {
        if (!$currentElement || !$currentElement.length) {
            $newElement.css('opacity', 0);
            this.fadeIn($newElement, transitionTime);
            return;
        }

        const $container = $currentElement.parent();
        const oldHeight = $currentElement.outerHeight(true);

        $container.css('height', oldHeight);

        $newElement.css({
            opacity: 0,
            transform: 'translateX(100%)'
        });

        $container.append($newElement);

        const newHeight = $newElement.outerHeight(true);
        $container.css('height', newHeight);

        this.fadeOut($currentElement, transitionTime);

        $currentElement.css({
            transition: `transform ${transitionTime}ms, opacity ${transitionTime}ms`,
            transform: 'translateX(-100%)',
            opacity: 0
        });

        $newElement.css({
            transition: `transform ${transitionTime}ms, opacity ${transitionTime}ms`,
            transform: 'translateX(0)',
            opacity: 1
        });

        setTimeout(() => {
            $currentElement.remove();
            $container.css({ height: 'unset' });
        }, transitionTime);
    },

	scrollToTop: function (top) {
		window.scroll({top: top, left: 0, behavior: 'smooth'});
	},

	scrollToElement: function ($element, {topOffset = 0}) {
		$element = $($element);
		const element = $element.get(0);

		if (!element) {
			return;
		}

		const top = element.getBoundingClientRect().top + window.scrollY;

		window.scroll({top: top + topOffset, left: 0, behavior: 'smooth'});
	},

	/**
	 * Scrolls a parent container to bring a specific child element to the top.
	 * This implementation is robust and correctly handles offsets regardless of
	 * the element's position within the container.
	 *
	 * @param {string|Cash} $parentElement The scrolling container.
	 * @param {string|Cash} $childElement The element to scroll to.
	 */
	scrollToChildElement($parentElement, $childElement) {
		const parentEl = $($parentElement).get(0);
		const childEl = $($childElement).get(0);

		if (!parentEl || !childEl) {
			return;
		}

		const parentRect = parentEl.getBoundingClientRect();
		const childRect = childEl.getBoundingClientRect();

		const topRelativeToParent = childRect.top - parentRect.top;
		const newScrollTop = parentEl.scrollTop + topRelativeToParent;

		parentEl.scrollTo({
			top: newScrollTop,
			behavior: 'smooth',
		});
	},

	slideToggle: function ($element, duration, callback) {
		const element = $($element).get(0);

		if (element.clientHeight === 0) {
			this._slide(element, duration, callback, true);
		} else {
			this._slide(element, duration, callback);
		}
	},

	slideUp: function ($element, duration, callback) {
		const element = $($element).get(0);
		this._slide(element, duration, callback);
	},

	slideDown: function ($element, duration, callback) {
		const element = $($element).get(0);
		this._slide(element, duration, callback, true);
	},

	_slide: function (el, duration, callback, isDown) {

		if (typeof duration === 'undefined') duration = 400;
		if (typeof isDown === 'undefined') isDown = false;

		el.style.overflow = 'hidden';
		if (isDown) el.style.display = 'block';

		const elStyles = window.getComputedStyle(el);

		const elHeight = parseFloat(elStyles.getPropertyValue('height'));
		const elPaddingTop = parseFloat(elStyles.getPropertyValue('padding-top'));
		const elPaddingBottom = parseFloat(elStyles.getPropertyValue('padding-bottom'));
		const elMarginTop = parseFloat(elStyles.getPropertyValue('margin-top'));
		const elMarginBottom = parseFloat(elStyles.getPropertyValue('margin-bottom'));

		const stepHeight = elHeight / duration;
		const stepPaddingTop = elPaddingTop / duration;
		const stepPaddingBottom = elPaddingBottom / duration;
		const stepMarginTop = elMarginTop / duration;
		const stepMarginBottom = elMarginBottom / duration;

		let start;

		function step(timestamp) {

			if (start === undefined) start = timestamp;

			const elapsed = timestamp - start;

			if (isDown) {
				el.style.height = (stepHeight * elapsed) + 'px';
				el.style.paddingTop = (stepPaddingTop * elapsed) + 'px';
				el.style.paddingBottom = (stepPaddingBottom * elapsed) + 'px';
				el.style.marginTop = (stepMarginTop * elapsed) + 'px';
				el.style.marginBottom = (stepMarginBottom * elapsed) + 'px';
			} else {
				el.style.height = elHeight - (stepHeight * elapsed) + 'px';
				el.style.paddingTop = elPaddingTop - (stepPaddingTop * elapsed) + 'px';
				el.style.paddingBottom = elPaddingBottom - (stepPaddingBottom * elapsed) + 'px';
				el.style.marginTop = elMarginTop - (stepMarginTop * elapsed) + 'px';
				el.style.marginBottom = elMarginBottom - (stepMarginBottom * elapsed) + 'px';
			}

			if (elapsed >= duration) {
				el.style.height = '';
				el.style.paddingTop = '';
				el.style.paddingBottom = '';
				el.style.marginTop = '';
				el.style.marginBottom = '';
				el.style.overflow = '';
				if (!isDown) el.style.display = 'none';
				if (typeof callback === 'function') callback();
			} else {
				window.requestAnimationFrame(step);
			}
		}

		window.requestAnimationFrame(step);
	},

	/**
	 * Prints a message to an element with a typing animation.
	 * The typing speed depends on the typingSpeed param.
	 *
	 * @param {Cash} $messageElement
	 * @param {string} message
	 * @param {number} typingSpeed
	 *
	 * @return {Promise}
	 */
	simulateTyping($messageElement, message, typingSpeed = 30) {
		return new Promise(resolve => {
			if (!message || !$messageElement || $messageElement.length === 0) {
				resolve();
				return;
			}

			$messageElement.text('');

			let i = 0;

			function typeCharacter() {
				if (i < message.length) {
					$messageElement.text($messageElement.text() + message.charAt(i));

					i++;
					setTimeout(typeCharacter, typingSpeed);
				} else {
					resolve();
				}
			}

			typeCharacter();
		});
	},

	/**
	 * Fades in with smooth height + opacity animation.
	 * @param {string|Cash} selector String selector or Cash object.
	 * @param {number} transitionTime Transition time in ms.
	 */
	slideFadeIn(selector, transitionTime = 200) {
		const $elements = $(selector);

		if (!$elements.length) {
			return;
		}

		$elements.each(function () {
			const $el = $(this);


			if ($el.css('display') !== 'none' && $el.css('visibility') !== 'hidden' && parseFloat($el.css('opacity')) !== 0) {
				return;
			}

			$el.show();

			const targetHeight = $el.outerHeight();

			$el.css({
				overflow: 'hidden',
				height: 0,
				opacity: 0,
				transition: `opacity ${transitionTime}ms, height ${transitionTime}ms`
			});

			void $el[0].offsetHeight;

			$el.css({
				height: targetHeight, opacity: 1
			});

			setTimeout(() => {
				$el.css({
					height: '', overflow: '', transition: ''
				});
			}, transitionTime);
		});
	},

	/**
	 * Fades out with smooth height + opacity animation.
	 * @param {string|Cash} selector String selector or Cash object.
	 * @param {number} transitionTime Transition time in ms.
	 */
	slideFadeOut(selector, transitionTime = 200) {
		const $elements = $(selector);

		if (!$elements.length) {
			return;
		}

		$elements.each(function () {
			const $el = $(this);

			const isVisible = $el.css('display') !== 'none';

			if (!isVisible) {
				$el.hide().css({
					height: '', overflow: '', transition: '', opacity: '',
				});
				return;
			}

			const startHeight = $el.outerHeight();

			$el.css({
				height: startHeight,
				overflow: 'hidden',
				opacity: parseFloat($el.css('opacity')) || 1,
				transition: `opacity ${transitionTime}ms, height ${transitionTime}ms`,
			});

			this.offsetHeight;

			$el.css({
				height: 0, opacity: 0,
			});

			setTimeout(() => {
				$el.hide();
				$el.css({
					height: '', overflow: '', transition: '', opacity: '',
				});
			}, transitionTime);
		});
	}
};

export default animate;
